import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { AddPhotoAlternate, Comment, Delete, Edit, Save, Search, Undo } from '@mui/icons-material'
import { Box, Button, FormControlLabel, Grid, InputAdornment, Skeleton, Stack, Switch, TextField, Typography } from '@mui/material'

import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import { ConfirmDialogData } from '../../../types/ConfirmDialogData'
import { useDeleteAnalysisScreenshotsMutation, useSaveAnalysisScreenshotsMutation } from '../../api/analysis-screenshots'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog/UnsavedChangesDialog'
import { ScreenshotCard, screenshotCardId } from '../../features/analysis-screenshots/components/ScreenshotCard/ScreenshotCard'
import { ScreenshotsHeader } from '../../features/analysis-screenshots/components/ScreenshotsHeader/ScreenshotsHeader'
import { Notice, WriteNotice } from '../../features/analysis-screenshots/components/WriteNotice/WriteNotice'
import {
  isUntagged,
  matchesScreenshotSearch,
  moveScreenshot,
  readLastViewedIndex,
  writeLastViewedIndex,
} from '../../features/analysis-screenshots/helpers/screenshotHelpers'
import { getAnalysisScreenshotsEditPath, getAnalysisScreenshotsUploadPath } from '../../features/analysis-screenshots/helpers/screenshotPaths'
import { getScreenshotsReadBackProblems, isScreenshotsChangedError, toPositions } from '../../features/analysis-screenshots/helpers/writeMessages'
import { useAnalysisScreenshotsData } from '../../features/analysis-screenshots/hooks/useAnalysisScreenshotsData'
import { getAnalysisOverviewPath } from '../../features/analysis/helpers/analysisPaths'
import { getScreenshotCommentEditPath } from '../../features/implementation-examples/helpers/implementationExamplePaths'
import { useEditPageLeave } from '../../hooks/useEditPageLeave'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'

type DeleteRequest = { ids: string[]; all?: boolean }

export const AnalysisScreenshotsPage: FC = () => {
  usePage(pageService.getInternalPageWithId('internal-analysis-screenshots'))
  const { analysisId = '' } = useParams<{ analysisId: string }>()
  return <AnalysisScreenshots key={analysisId} analysisId={analysisId} />
}

const AnalysisScreenshots: FC<{ analysisId: string }> = ({ analysisId }) => {
  const { t } = useTranslation()
  const { notifySuccess } = useMutationFeedback()
  const navigate = useNavigate()
  const data = useAnalysisScreenshotsData(analysisId)
  const [saveScreenshots, saveState] = useSaveAnalysisScreenshotsMutation()
  const [deleteScreenshots, deleteState] = useDeleteAnalysisScreenshotsMutation()
  // Ids in the order being edited; undefined while it's the stored order.
  const [order, setOrder] = useState<string[]>()
  const [checkedIds, setCheckedIds] = useState<string[]>([])
  const [search, setSearch] = useState('')
  const [untaggedOnly, setUntaggedOnly] = useState(false)
  const [deleteRequest, setDeleteRequest] = useState<ConfirmDialogData<DeleteRequest>>()
  const [notice, setNotice] = useState<Notice>()
  const [lastViewedIndex, setLastViewedIndex] = useState<number>()
  const scrolledRef = useRef(false)
  const { guard } = useEditPageLeave(!!order)

  const gameId = data.analysis?.gameId ?? ''
  const marketIso = data.analysis?.marketIso
  const storedIds = useMemo(() => data.items.map(({ screenshotId }) => screenshotId), [data.items])
  const storedIndexById = useMemo(() => new Map(storedIds.map((id, index) => [id, index])), [storedIds])
  const orderedViews = useMemo(() => {
    if (!order) return data.views
    const byId = new Map(data.views.map((view) => [view.item.screenshotId, view]))
    return order.flatMap((id) => byId.get(id) ?? [])
  }, [data.views, order])
  const orderedIndexById = useMemo(() => new Map(orderedViews.map((view, index) => [view.item.screenshotId, index])), [orderedViews])
  const visibleViews = useMemo(
    () => orderedViews.filter((view) => (!untaggedOnly || isUntagged(view)) && matchesScreenshotSearch(view, search)),
    [orderedViews, untaggedOnly, search]
  )
  const isFiltering = untaggedOnly || !!search.trim()
  const isBusy = saveState.isLoading || deleteState.isLoading || data.isRefreshing
  const checkedIdSet = useMemo(() => new Set(checkedIds), [checkedIds])
  const checkedInOrder = useMemo(() => storedIds.filter((id) => checkedIdSet.has(id)), [storedIds, checkedIdSet])

  // A stored list that no longer has the reordered ids means the order is stale.
  useEffect(() => {
    if (order && (order.length !== storedIds.length || order.some((id) => !storedIndexById.has(id)))) setOrder(undefined)
    setCheckedIds((ids) => (ids.every((id) => storedIndexById.has(id)) ? ids : ids.filter((id) => storedIndexById.has(id))))
  }, [order, storedIds.length, storedIndexById])

  useEffect(() => {
    if (scrolledRef.current || data.isLoading || !gameId) return
    scrolledRef.current = true
    const index = readLastViewedIndex(gameId)
    const view = index === undefined ? undefined : data.views[index]
    if (!view) return
    setLastViewedIndex(index)
    document.getElementById(screenshotCardId(view.item.screenshotId))?.scrollIntoView?.({ block: 'center' })
  }, [data.isLoading, data.views, gameId])

  const handleSaveOrder = async () => {
    if (!order) return
    const byId = new Map(data.items.map((item) => [item.screenshotId, item]))
    const body = order.flatMap((id) => byId.get(id) ?? [])
    try {
      const { readBack, stored } = await saveScreenshots({ analysisId, loaded: data.items, body }).unwrap()
      const problems = getScreenshotsReadBackProblems(readBack, toPositions(stored.map(({ screenshotId }) => screenshotId)))
      setOrder(undefined)
      if (problems.length) setNotice({ severity: 'error', messages: [{ key: 'internal-analysts:analysis_screenshots_order_not_stored' }, ...problems] })
      else {
        setNotice(undefined)
        notifySuccess(t('internal-analysts:analysis_screenshots_order_saved'))
      }
    } catch (error) {
      const changed = isScreenshotsChangedError(error)
      if (changed) setOrder(undefined)
      setNotice({
        severity: 'error',
        messages: [{ key: changed ? 'internal-analysts:analysis_screenshots_changed' : 'internal-analysts:analysis_screenshots_order_save_failed' }],
      })
    }
  }

  const handleDelete = async (request: DeleteRequest) => {
    try {
      const { stillListedIds } = await deleteScreenshots({ analysisId, screenshotIds: request.ids }).unwrap()
      setCheckedIds([])
      if (stillListedIds.length) {
        setNotice({
          severity: 'error',
          messages: [
            {
              key: 'internal-analysts:analysis_screenshots_still_listed',
              options: { count: stillListedIds.length, positions: toPositions(storedIds)(stillListedIds) },
            },
          ],
        })
      } else {
        setNotice(undefined)
        notifySuccess(t('internal-analysts:analysis_screenshots_deleted', { count: request.ids.length }))
      }
    } catch {
      setNotice({ severity: 'error', messages: [{ key: 'internal-analysts:analysis_screenshots_delete_failed' }] })
    }
  }

  const askDelete = (ids: string[], all = false) =>
    setDeleteRequest({
      title: t(all ? 'internal-analysts:analysis_screenshots_delete_all_title' : 'internal-analysts:analysis_screenshots_delete_title', { count: ids.length }),
      confirmText: t('internal-analysts:analysis_screenshots_delete_confirm', { count: ids.length }),
      actionText: t('internal-common:delete'),
      destructiveAction: true,
      data: { ids, all },
    })

  const openEdit = (indices: number[]) => {
    if (indices.length === 1) writeLastViewedIndex(gameId, indices[0])
    navigate(getAnalysisScreenshotsEditPath(analysisId, indices))
  }

  const back = () => navigate(getAnalysisOverviewPath(analysisId))

  if (data.error) return <Typography color="error.main">{t(`internal-analysts:analysis_screenshots_load_failed_${data.error}`)}</Typography>
  if (data.isLoading || !data.analysis || !data.game) return <Skeleton variant="rectangular" height={480} />

  const selectionDisabled = isBusy || !!order

  return (
    <Box pb={8}>
      <ScreenshotsHeader
        analysis={data.analysis}
        game={data.game}
        backLabel={t('internal-analysts:analysis_screenshots_back_to_overview')}
        onBack={back}
        detail={t('internal-analysts:analysis_overview_analysis_screenshots', { count: data.items.length })}
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<AddPhotoAlternate />}
              disabled={isBusy || !!order}
              onClick={() => navigate(getAnalysisScreenshotsUploadPath(analysisId))}
            >
              {t('internal-analysts:analysis_screenshots_upload')}
            </Button>
            {order && (
              <Button startIcon={<Undo />} disabled={isBusy} onClick={() => setOrder(undefined)}>
                {t('internal-analysts:analysis_screenshots_reset_order')}
              </Button>
            )}
            <Button variant="contained" startIcon={<Save />} disabled={isBusy || !order} onClick={handleSaveOrder}>
              {t('internal-analysts:analysis_screenshots_save_order')}
            </Button>
          </>
        }
      />
      <WriteNotice notice={notice} onClose={() => setNotice(undefined)} />

      <Box sx={{ position: 'sticky', top: 0, zIndex: 2, bgcolor: 'background.paper', py: 1, mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('internal-analysts:analysis_screenshots_search')}
            inputProps={{ 'aria-label': t('internal-analysts:analysis_screenshots_search') }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControlLabel
            control={<Switch checked={untaggedOnly} onChange={(event) => setUntaggedOnly(event.target.checked)} />}
            label={t('internal-analysts:analysis_screenshots_untagged_only')}
          />
          <Button size="small" disabled={selectionDisabled} onClick={() => setCheckedIds(visibleViews.map((view) => view.item.screenshotId))}>
            {t('internal-analysts:analysis_screenshots_select_all')}
          </Button>
          <Button size="small" disabled={selectionDisabled || !checkedIds.length} onClick={() => setCheckedIds([])}>
            {t('internal-analysts:analysis_screenshots_select_none')}
          </Button>
          <Typography variant="body2" color="text.secondary">
            {t('internal-analysts:analysis_screenshots_selected', { count: checkedInOrder.length, shown: visibleViews.length })}
          </Typography>
          <Box flex={1} />
          <Button
            size="small"
            variant="outlined"
            startIcon={<Edit />}
            disabled={selectionDisabled || !checkedInOrder.length}
            onClick={() => openEdit(checkedInOrder.map((id) => storedIndexById.get(id) ?? -1))}
          >
            {t('internal-analysts:analysis_screenshots_edit_selected')}
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Comment />}
            disabled={!checkedInOrder.length}
            href={getScreenshotCommentEditPath({ gameId, screenshotIds: checkedInOrder, marketIso })}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('internal-analysts:analysis_screenshots_comment_selected')}
          </Button>
          <Button
            size="small"
            color="error"
            startIcon={<Delete />}
            disabled={selectionDisabled || !checkedInOrder.length}
            onClick={() => askDelete(checkedInOrder)}
          >
            {t('internal-analysts:analysis_screenshots_delete_selected')}
          </Button>
          <Button size="small" color="error" variant="outlined" disabled={selectionDisabled || !storedIds.length} onClick={() => askDelete(storedIds, true)}>
            {t('internal-analysts:analysis_screenshots_delete_all')}
          </Button>
        </Stack>
        {order && (
          <Typography variant="body2" color="warning.main" mt={1}>
            {t('internal-analysts:analysis_screenshots_order_unsaved')}
          </Typography>
        )}
        {isFiltering && (
          <Typography variant="body2" color="text.secondary" mt={1}>
            {t('internal-analysts:analysis_screenshots_move_off_while_filtering')}
          </Typography>
        )}
      </Box>

      {!data.items.length ? (
        <Typography color="text.secondary">{t('internal-analysts:analysis_screenshots_none')}</Typography>
      ) : !visibleViews.length ? (
        <Typography color="text.secondary">{t('internal-analysts:analysis_screenshots_none_match')}</Typography>
      ) : (
        <Grid container spacing={2} wrap="wrap">
          {visibleViews.map((view) => {
            const id = view.item.screenshotId
            const position = orderedIndexById.get(id) ?? 0
            return (
              <Grid item xs={12} sm={6} md={4} xl={3} key={id}>
                <ScreenshotCard
                  view={view}
                  position={position + 1}
                  checked={checkedIdSet.has(id)}
                  highlighted={!order && view.index === lastViewedIndex}
                  disabled={isBusy}
                  lockActions={!!order}
                  moveDisabled={isFiltering}
                  canMoveUp={position > 0}
                  canMoveDown={position < orderedViews.length - 1}
                  commentUrl={getScreenshotCommentEditPath({ gameId, screenshotIds: [id], marketIso })}
                  onCheck={(checked) => setCheckedIds((ids) => (checked ? [...ids, id] : ids.filter((candidate) => candidate !== id)))}
                  onMoveUp={() => setOrder(moveScreenshot(orderedViews, position, -1).map(({ item }) => item.screenshotId))}
                  onMoveDown={() => setOrder(moveScreenshot(orderedViews, position, 1).map(({ item }) => item.screenshotId))}
                  onEdit={() => openEdit([view.index])}
                  onComment={() => writeLastViewedIndex(gameId, view.index)}
                  onDelete={() => askDelete([id])}
                />
              </Grid>
            )
          })}
        </Grid>
      )}

      <ConfirmDialog
        open={!!deleteRequest}
        confirmDialogData={deleteRequest}
        onClose={(confirmed) => {
          setDeleteRequest(undefined)
          if (confirmed?.data) handleDelete(confirmed.data)
        }}
      />
      <UnsavedChangesDialog guard={guard} />
    </Box>
  )
}
